import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';
import useBasketContext from '../../../../../contexts/basket/useBasketContext';
import useGroupContext from '../../../../../contexts/group/useGroupContext';
import useOperatorContext from '../../../../../contexts/operator/useOperatorContext';
import { money } from '../../../../../services/format';
import { groupLockedMessage } from '../../../../../services/groupOrder';
import CopyLinkButton from './CopyLinkButton';
import { CancelGroupModal, LeaveGroupModal } from './GroupWarningModals';
import useShareLink from './useShareLink';

// Under the site's own banner: what the group order is, with what the lead and the guests can do with it
const GroupBanner = () => {
    const groupContext = useGroupContext();
    const basketContext = useBasketContext();
    const operatorContext = useOperatorContext();
    const navigate = useNavigate();
    const link = useShareLink();

    const [cancelShow, setCancelShow] = useState(false);
    const [leaveShow, setLeaveShow] = useState(false);

    if (!groupContext.active) return null;

    const items = basketContext.basketItems.reduce((count, item) => count + (item.quantity ?? 0), 0);

    // over: back to the start, with nothing left of it
    const finish = () => {
        basketContext.clearBasket();
        navigate('/');
    };

    const host = groupContext.host ?? t`The host`;

    return (
        <Banner className="groupBanner">
            <div className="groupBanner__inner">
                <span className="material-symbols-outlined groupBanner__icon">group</span>
                <div className="groupBanner__text">
                    {groupContext.isLead ? (
                        <>
                            <strong>{t`Your group order`}</strong>
                            <span>
                                {groupContext.guestCount === 1 ? t`1 guest` : t`${groupContext.guestCount} guests`}
                                {' · '}
                                {money(basketContext.price.subtotal, operatorContext.operator?.ccy, operatorContext.operator?.locale)}
                            </span>
                        </>
                    ) : (
                        <>
                            <strong>{t`${host}'s group order`}</strong>
                            <span>
                                {groupContext.locked
                                    ? groupLockedMessage(groupContext.host)
                                    : items === 1 ? t`You added 1 item` : t`You added ${items} items`}
                            </span>
                        </>
                    )}
                </div>
                <div className="groupBanner__actions">
                    {groupContext.isLead && <CopyLinkButton link={link} label={t`Copy share link`} small secondary />}
                    <button type="button" onClick={() => groupContext.isLead ? setCancelShow(true) : setLeaveShow(true)}>
                        {groupContext.isLead ? t`Cancel order` : t`Leave order`}
                    </button>
                </div>
            </div>
            <CancelGroupModal
                show={cancelShow}
                onHide={() => setCancelShow(false)}
                onConfirm={async () => {
                    await groupContext.cancel();
                    setCancelShow(false);
                    finish();
                }}
            />
            <LeaveGroupModal
                show={leaveShow}
                onHide={() => setLeaveShow(false)}
                onConfirm={async () => {
                    await groupContext.leave();
                    setLeaveShow(false);
                    finish();
                }}
            />
        </Banner>
    );
};

const Banner = styled.div`
    width: 100%;
    background-color: var(--ui_banner_bg);
    color: var(--ui_banner_color);
    border-bottom: var(--layouts_nav_border);

    .groupBanner__inner {
        max-width: 1296px;
        margin: 0 auto;
        padding: 12px 16px;
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;

        @media screen and (min-width: 768px) {
            padding: 12px 48px;
        }
    }

    .groupBanner__icon {
        font-size: 24px;
        line-height: 24px;
    }

    .groupBanner__text {
        flex: 1;
        min-width: 180px;
        display: flex;
        flex-direction: column;
        font-size: 14px;
        line-height: 20px;

        strong {
            font-size: 16px;
            line-height: 24px;
        }
    }

    .groupBanner__actions {
        display: flex;
        gap: 8px;
        align-items: center;

        button {
            width: auto;
            height: 40px;
            padding: 0 16px;
            font-family: var(--text_heading_font);
            letter-spacing: var(--text_heading_font_spacing);
            text-transform: var(--text_heading_font_case);
            font-size: 14px;
            line-height: 20px;
            background: transparent;
            color: var(--ui_banner_color);
            border: 1px solid currentColor;
            border-radius: var(--radius_m);
            cursor: pointer;

            &:hover {
                background: rgba(127, 127, 127, 0.15);
            }
        }
    }
`;

export default GroupBanner;
